"use client";

import NoImageSrc from "@/assets/images/no_image.svg";
import bgImage from "@/assets/svgs/clp-blue-beige-pattern-breezy.svg";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { useEffect, useRef, useState } from "react";
import { CLink } from "../common/customLink/CustomLink";
import { pdpEditsSignal } from "../navbar/Navbar";
import { useEditsChildWidth } from "@/hooks/useEditsChildWidth";
import CldImage from "../ui/cldImg/CldImage";
import RenderCurrencyInText from "../common/RenderCurrencyInText";
import ResponsiveCarousel from "../ui/responsiveCarousel/ResponsiveCarousel";

interface PLPHeadeTab {
  src: string;
  url: string;
  alt?: string;
  label?: string;
  index?: number;
  active?: boolean;
  selectedEdit?: number | null;
  handleSelectedEdits?: (index: number) => void;
}

type EditsType = "withImageGrid" | "withIcons" | "withTabs";

interface PLPHeaderProps {
  variant: EditsType;
  tabs: PLPHeadeTab[];
  background?: {
    type: "image" | "color";
    backgroundUrl?: string;
    backgroundColor?: string;
  };
}

const PLPHeader = ({ variant, tabs, background }: PLPHeaderProps) => {
  // states
  const [isCollapsed, setIsCollapsed] = useState(false);

  const tabsRef = useRef(null);
  const { isEditsCenter: isTabsCenter } = useEditsChildWidth(tabsRef);
  const divRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialOffsetTopRef = useRef<number | null>(null);
  const isCollapsedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // methods
  function handleSetPdpEditsTransitionState(val: boolean) {
    pdpEditsSignal.setSignal(val);
  }

  // lifecycles
  useEffect(() => {
    if (containerRef.current && initialOffsetTopRef.current === null) {
      initialOffsetTopRef.current = containerRef.current.getBoundingClientRect().top + window.scrollY;
    }

    const handleScroll = () => {
      if (containerRef.current && initialOffsetTopRef.current !== null) {
        const stickyParent = containerRef.current.closest("#plp-edit-section") as HTMLElement | null;
        const stickyTopValue = stickyParent ? parseFloat(stickyParent.style.top || "0") : 0;
        const threshold = initialOffsetTopRef.current - stickyTopValue;
        const buffer = 100;

        if (!isCollapsedRef.current && window.scrollY > threshold) {
          isCollapsedRef.current = true;
          setIsCollapsed(true);
        } else if (isCollapsedRef.current && window.scrollY < threshold - buffer) {
          isCollapsedRef.current = false;
          setIsCollapsed(false);
        }
      } else {
        setIsCollapsed(window.scrollY > 150);
      }
    };

    abortControllerRef.current = new AbortController();

    window.addEventListener("scroll", handleScroll, { signal: abortControllerRef.current.signal });

    divRef.current?.addEventListener(
      "transitionstart",
      () => handleSetPdpEditsTransitionState(true),
      { signal: abortControllerRef.current.signal },
    );
    divRef.current?.addEventListener(
      "transitionend",
      () => handleSetPdpEditsTransitionState(false),
      { signal: abortControllerRef.current.signal },
    );
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  function getHeight(variant: EditsType, isCollapsed: boolean) {
    if (variant === "withTabs") return "h-[84px]";
    else if (isCollapsed) return "h-[84px]";
    else if (variant === "withIcons") return "h-[8.438rem] sm:h-[12.5rem]";
    return "h-[9.788rem] sm:h-[17.95rem]";
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "z-30 w-full bg-white transition-[height] duration-500 ease-in-out",
        getHeight(variant, isCollapsed),
      )}
    >
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
        {/* Icons or Image Grid */}
        <div
          className={`absolute flex h-full w-full items-center justify-center transition-[scale,opacity] duration-500 ease-in-out ${isCollapsed ? "scale-0 opacity-0" : "scale-100 opacity-100"}`}
          style={
            background?.type === "image" && background?.backgroundUrl
              ? { backgroundImage: `url(${background.backgroundUrl})`, backgroundSize: "cover" }
              : background?.type === "color" && background?.backgroundColor
                ? { backgroundColor: background.backgroundColor }
                : variant === "withIcons"
                  ? { backgroundImage: `url(${bgImage.src})` }
                  : {}
          }
        >
          {variant === "withIcons" ? (
            <div className="container px-4 md:px-12">
              <ResponsiveCarousel gap={{ mobile: 32, desktop: 89 }}>
                {tabs.map((icon, i) => {
                  const labelClass = cn(
                    "text-center text-xs font-normal leading-normal tracking-[-0.3px] text-[#3A3A3A] transition-colors md:text-sm",
                    icon.active && "text-tertiary-13",
                    "group-hover:text-tertiary-13",
                  );
                  return (
                    <div
                      key={`icon_${i}_${icon.label}`}
                      className="group flex cursor-pointer flex-col items-center justify-center gap-4"
                      onClick={() => {
                        if (icon.handleSelectedEdits !== undefined && icon.index !== undefined) {
                          icon.handleSelectedEdits(icon.index);
                        }
                      }}
                    >
                      <div
                        className={cn(
                          "flex size-[5.75rem] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white outline outline-[1.5px] outline-transparent [-webkit-outline-offset:-1.5px] outline-offset-[-1.5px] transition-[outline-color] max-sm:size-[4.5rem]",
                          "group-hover:outline-tertiary-13",
                          icon.active && "outline-tertiary-13",
                        )}
                      >
                        <CLink href={icon?.url ?? ""} className="block size-full overflow-hidden rounded-full">
                          <CldImage
                            width={92}
                            height={92}
                            src={icon?.src ? getCloudinaryURL(icon.src) : NoImageSrc}
                            alt={icon.alt ?? ""}
                            className="size-full object-cover object-center rounded-full"
                          />
                        </CLink>
                      </div>
                      {icon.url !== undefined && (
                        <CLink href={icon.url}>
                          <p className={labelClass}><RenderCurrencyInText text={icon.label} /></p>
                        </CLink>
                      )}
                      {!icon.url && <p className={labelClass}><RenderCurrencyInText text={icon.label} /></p>}
                    </div>
                  );
                })}
              </ResponsiveCarousel>
            </div>
          ) : variant === "withImageGrid" ? (
            <div className="container flex h-[9.688rem] w-full items-center justify-center sm:h-[17.75rem]">
              <ResponsiveCarousel gap={{ mobile: 9, desktop: 16 }}>
                {tabs.map((c, i) => (
                  <div
                    key={i}
                    className="flex flex-shrink-0 cursor-pointer items-center justify-center space-y-2"
                    onClick={() => {
                      if (c.handleSelectedEdits !== undefined && c.index !== undefined) {
                        c.handleSelectedEdits(c.index);
                      }
                    }}
                  >
                    <CLink
                      href={c.url ?? ""}
                      className="flex flex-col items-center justify-center gap-2 md:gap-4"
                    >
                      <div className="flex h-[8.188rem] w-[5.438rem] items-center justify-center md:h-[15.438rem] md:w-[13.25rem]">
                        <CldImage
                          width={87}
                          src={c?.src ? getCloudinaryURL(c.src) : NoImageSrc}
                          alt={c.alt ?? ""}
                          height={131}
                          className={cn(
                            "h-[8.188rem] w-[5.438] object-contain md:h-[15.438rem] md:w-[13.25rem]",
                            c.active ? "border-[1px] border-tertiary-13" : "",
                          )}
                        />
                      </div>
                      <p
                        className={cn(
                          "text-xs font-medium md:text-base",
                          c.active ? "text-tertiary-13" : "",
                        )}
                      >
                        <RenderCurrencyInText text={c.label} />
                      </p>
                    </CLink>
                  </div>
                ))}
              </ResponsiveCarousel>
            </div>
          ) : (
            <></>
          )}
        </div>

        {/* Tabs Transition */}
        <div
          ref={divRef}
          className={cn(
            "absolute flex w-full transition-[opacity,transform,scale] duration-500 ease-in-out md:w-auto",
            variant === "withTabs"
              ? "scale-100 opacity-100"
              : isCollapsed
                ? "scale-100 opacity-100"
                : "pointer-events-none scale-150 opacity-0",
          )}
        >
          <div
            className={cn(
              "no-scrollbar flex w-full items-center gap-3 overflow-auto whitespace-nowrap px-4 md:justify-center",
              isTabsCenter ? "justify-center" : "justify-start",
            )}
            ref={tabsRef}
          >
            {tabs.map((t, i) => (
              <CLink href={t.url} key={`tab_${i}_${t.url}`}>
                <div
                  className={cn(
                    "flex h-10 flex-shrink-0 items-center justify-center whitespace-nowrap px-2 py-[9px] text-center text-xs md:px-5 md:py-3 md:text-sm",
                    t.active
                      ? "border-[1px] border-tertiary-13 text-tertiary-13"
                      : "border-[1px] border-black",
                  )}
                  onClick={() => {
                    if (t.handleSelectedEdits !== undefined && t.index !== undefined) {
                      t.handleSelectedEdits(t.index);
                    }
                  }}
                >
                  <RenderCurrencyInText text={t.label} />
                </div>
              </CLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PLPHeader;
